import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { MarketActionCard, MarketList, MarketRow } from '@market/react';
import { getLocalePath } from 'utilities/locales';
import { checkAddOnsForActiveSubscription } from 'components/pages/product/product_with_software_add_on/utils';
import { getPlanPrice, getSoftwarePlanLineItems } from 'utilities/software';
import { toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';

const NO_ADD_ON_VALUE = '0';

const SoftwareAddOn = ({
  category,
  addOns,
  selected = NO_ADD_ON_VALUE,
  onSelect = () => {},
  yesAsFirstOption = false, // if we want it to be yes/no vs no/yes
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { currentUser } = useProductContext();
  const isPnpSubscribed = Boolean(currentUser?.is_pnp_subscribed);

  if (addOns.length === 0) {
    return null;
  }

  if (checkAddOnsForActiveSubscription(addOns)) {
    return null;
  }

  const renderAddonSubtext = () => {
    const linkKey = `relations.addOn.subtext.${category}`;

    return i18n.exists(linkKey) ? (
      <p
        dangerouslySetInnerHTML={{
          __html: t(linkKey, { locale: getLocalePath() }),
        }}
        className="paragraph-20"
        style={{ marginTop: theme.spacing(1) }}
      />
    ) : null;
  };

  // Format a Money proto (`{ amount, currency_code }`). The proto field is a
  // string ISO code (e.g. "USD"); pass it straight through. Wrapping in
  // `Currency[...]` flips it to the numeric ISO 4217 value, which Dinero
  // rejects with "Invalid currency code".
  const formatLineCost = (cost) =>
    cost
      ? toCurrencyWithMinorUnitAmount(cost.amount, cost.currency_code)
      : null;

  const renderAddOnCard = (addOn) => {
    const lineItems = getSoftwarePlanLineItems(addOn.plan, {
      isPnpSubscribed,
    });

    const baseLine = lineItems.find((item) => item.isBase);
    const deviceLine = lineItems.find(
      (item) => !item.isBase && item.unitType === 'FNB_KIOSK_DEVICE',
    );

    // Kiosk free-seller state — only reachable when the BE has surfaced a
    // multi-bucket Plus/Premium plan (i.e. saas-shop-kiosk-improvements is on
    // AND the merchant is not already a P&P subscriber). Combined total on
    // the right, per-bucket breakdown in the body.
    if (baseLine && deviceLine) {
      const combinedFormatted = formatLineCost({
        amount: baseLine.cost.amount + deviceLine.cost.amount,
        currency_code: deviceLine.cost.currency_code,
      });

      return (
        <MarketActionCard
          key={addOn.variant_id.toString()}
          value={addOn.variant_id.toString()}
        >
          <MarketRow>
            <p slot="label">
              {t(
                `relations.addOnOverride.${addOn.base_product_key}.subscriptionLabelWithBase`,
              )}
            </p>
            <div slot="subtext">
              <p className="paragraph-20">
                {t(
                  `relations.addOnOverride.${addOn.base_product_key}.basePriceBreakdown`,
                  { price: formatLineCost(baseLine.cost) },
                )}
              </p>
              <p className="paragraph-20">
                {t(
                  `relations.addOnOverride.${addOn.base_product_key}.devicePriceBreakdown`,
                  { price: formatLineCost(deviceLine.cost) },
                )}
              </p>
            </div>
            {combinedFormatted && (
              <p
                slot="side-label"
                className="medium-30"
                style={{ whiteSpace: 'nowrap' }}
              >
                {`+${combinedFormatted}`}
              </p>
            )}
          </MarketRow>
        </MarketActionCard>
      );
    }

    // Default layout — FF off (legacy fnb-kiosk plan), P&P subscriber state,
    // or any non-kiosk add-on. Uses `getPlanPrice` so the rendering is
    // identical to the pre-PR pre-flag path.
    return (
      <MarketActionCard
        key={addOn.variant_id.toString()}
        value={addOn.variant_id.toString()}
      >
        <MarketRow>
          <p slot="label">{t('productPage.addSoftwareAddOn')}</p>
          <p
            slot="side-label"
            className="medium-30"
            style={{ whiteSpace: 'nowrap' }}
          >
            {`+${t(`relations.addOnOverride.${addOn.base_product_key}.price`, {
              price: getPlanPrice(addOn.plan),
            })}`}
          </p>
        </MarketRow>
      </MarketActionCard>
    );
  };

  const renderAddons = () => {
    const NoOption = (
      <MarketActionCard value={NO_ADD_ON_VALUE}>
        <MarketRow>
          <p slot="label">{t(`relations.addOn.noText.${category}`)}</p>
        </MarketRow>
      </MarketActionCard>
    );

    const YesOptions = addOns.map(renderAddOnCard);

    const options = yesAsFirstOption
      ? [YesOptions, NoOption]
      : [NoOption, YesOptions];

    return options.flat();
  };

  return (
    <div key={category}>
      <p className="medium-30" style={{ margin: theme.spacing(2, 0) }}>
        {t(`relations.addOn.label.${category}`)}
      </p>
      <MarketList
        interactive
        value={selected}
        onMarketListSelectionsDidChange={(event) => {
          const {
            detail: { newSelection, newSelectionValue },
            target,
          } = event;

          onSelect({
            category: target.dataset.category,
            newVariantId: newSelectionValue,
            choice: newSelection.innerText,
          });
        }}
        data-category={category}
        data-testid="add-on-list"
      >
        {renderAddons()}
        {renderAddonSubtext()}
      </MarketList>
    </div>
  );
};

export { SoftwareAddOn as default, NO_ADD_ON_VALUE };
